import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { UserService } from '../../shared/services/user.service';
import { AdditionalService } from '../../shared/services/additional.service';
import { GoogleUnlinkAccountModalComponent } from '../../shared/components/modals/google-unlink-account-modal/google-unlink-account-modal.component';
import { ChangePasswordModalComponent } from '../../shared/components/modals/change-password-modal/change-password-modal.component';
import { RULES } from '../../shared/constants';
import { AuthApiService } from '../../shared/services/auth-api.service';

@Component({
  selector: 'app-user-profile-edit-page',
  templateUrl: './user-profile-edit-page.component.html',
  styleUrls: ['./user-profile-edit-page.component.scss']
})
export class UserProfileEditPageComponent implements OnInit, OnDestroy {
  protected readonly RULES = RULES;

  private userInfoSub?: Subscription;
  private googleLinkSub?: Subscription;

  form: FormGroup = new FormGroup({});

  constructor(
    public userService: UserService,
    public additionalService: AdditionalService,
    private readonly authService: AuthService,
    private readonly authApiService: AuthApiService,
    private readonly dialogService: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.userInfoSub = this.userService.userInfo$.subscribe({
      next: (data) => {
        if (data) {
          this.form = new FormGroup({
            email: new FormControl({ value: data.email, disabled: true }),
            firstName: new FormControl(data.firstName, [
              Validators.required,
              Validators.minLength(RULES.USER_NAME_MIN_LENGTH),
              Validators.maxLength(RULES.USER_NAME_MAX_LENGTH),
              Validators.pattern(RULES.USER_NAME_REGEX)
            ]),
            lastName: new FormControl(data.lastName, [
              Validators.minLength(RULES.USER_NAME_MIN_LENGTH),
              Validators.maxLength(RULES.USER_NAME_MAX_LENGTH),
              Validators.pattern(RULES.USER_NAME_REGEX)
            ])
          });
        }
      }
    });

    this.googleLinkSub = this.authService.googleLinkSuccess$.subscribe(() => {
      this.userService.getUserInfo();
    });
  }

  onSetPassword() {
    this.authApiService.resetPasswordRequest({ email: this.form.get('email')?.value }).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastrService.success(this.translate.instant('messages.password_reset_sent'));
        } else {
          this.toastrService.error(response.error?.description);
        }
      }
    });
  }

  onChangePassword() {
    this.dialogService.open(ChangePasswordModalComponent, {
      panelClass: ['dialog-overlay-pane'],
      width: '500px'
    });
  }

  onUnlinkGoogle() {
    this.dialogService.open(GoogleUnlinkAccountModalComponent, {
      panelClass: ['dialog-overlay-pane'],
      data: {
        email: this.userService.userInfo$.getValue()!.email
      },
      width: '500px'
    });
  }

  onSubmit() {
    if (!this.form.valid) {
      this.additionalService.markFormGroupAsDirty(this.form);
      return;
    }

    const formData = this.additionalService.cleanFormData(this.form.value);

    this.userService.updateUserInfoRequest(formData).subscribe((response) => {
      const translatedName = this.translate.instant(`messages.instances.account`);

      if (response.success && response.data) {
        this.userService.getUserInfo();

        this.toastrService.success(this.translate.instant('messages.updated_success', { name: translatedName }));
      } else {
        this.toastrService.error(this.translate.instant('messages.update_failed', { name: translatedName.toLowerCase() }));
      }
    });
  }

  ngOnDestroy(): void {
    this.userInfoSub?.unsubscribe();
    this.googleLinkSub?.unsubscribe();
  }
}
